/**
 * Advanced Milestone Section Component
 * Professional implementation with modern JavaScript features
 * Includes accessibility, performance optimizations, and error handling
 */

class MilestoneSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.id;
    this.isInitialized = false;
    this.isScrolling = false;
    this.isUserScrolling = false; // Track user-initiated scrolling
    this.activeIndex = 0;
    this.lastScrollTime = 0; // Track scroll timing for stability
    
    // Slider properties
    this.isSliderMode = false;
    this.sliderContainer = null;
    this.sliderWrapper = null;
    this.sliderDots = [];
    this.sliderPrevBtn = null;
    this.sliderNextBtn = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.sliderTransition = false;
    
    // DOM elements
    this.yearsNav = null;
    this.yearButtons = [];
    this.milestoneItems = [];
    this.parallaxElement = null;
    
    // Performance optimization
    this.scrollThrottleDelay = 32; // ~30fps - more stable for fast scrolling
    this.resizeDebounceDelay = 250;
    this.intersectionThreshold = 0.2;
    
    // Event handlers (bound to preserve context)
    this.handleScroll = this.throttle(this.handleScroll.bind(this), this.scrollThrottleDelay);
    this.handleResize = this.debounce(this.handleResize.bind(this), this.resizeDebounceDelay);
    this.handleYearClick = this.handleYearClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleSliderPrev = this.handleSliderPrev.bind(this);
    this.handleSliderNext = this.handleSliderNext.bind(this);
    this.handleDotClick = this.handleDotClick.bind(this);
    
    // Initialize the component
    this.init();
  }

  /**
   * Initialize the milestone section
   */
  init() {
    try {
      this.cacheElements();
      this.validateElements();
      this.checkSliderMode();
      
      if (this.isSliderMode) {
        this.initializeSlider();
      } else {
        this.setupEventListeners();
        this.initializeIntersectionObserver();
      }
      
      this.setInitialState();
      this.isInitialized = true;
      
      console.log(`Milestone section ${this.sectionId} initialized successfully in ${this.isSliderMode ? 'slider' : 'scroll'} mode`);
    } catch (error) {
      console.error(`Failed to initialize milestone section ${this.sectionId}:`, error);
    }
  }

  /**
   * Cache DOM elements for better performance
   */
  cacheElements() {
    this.yearsNav = this.section.querySelector('[data-milestone-years]');
    this.yearButtons = Array.from(this.section.querySelectorAll('[data-year-link]'));
    this.milestoneItems = Array.from(this.section.querySelectorAll('.milestone__item'));
    this.parallaxElement = this.section.querySelector('[data-parallax]');
  }

  /**
   * Validate that required elements exist
   */
  validateElements() {
    if (!this.yearsNav) {
      throw new Error('Years navigation element not found');
    }
    if (this.yearButtons.length === 0) {
      throw new Error('No year buttons found');
    }
    if (this.milestoneItems.length === 0) {
      throw new Error('No milestone items found');
    }
    if (this.yearButtons.length !== this.milestoneItems.length) {
      throw new Error('Mismatch between year buttons and milestone items');
    }
  }

  /**
   * Setup event listeners with proper cleanup tracking
   */
  setupEventListeners() {
    // Year button clicks
    this.yearButtons.forEach((button, index) => {
      button.addEventListener('click', this.handleYearClick);
      button.addEventListener('keydown', this.handleKeyDown);
      
      // Add accessibility attributes
      button.setAttribute('role', 'tab');
      button.setAttribute('tabindex', index === 0 ? '0' : '-1');
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.setAttribute('aria-controls', this.milestoneItems[index].id);
    });

    // Scroll and resize events
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });

    // Keyboard navigation for the entire section
    this.section.addEventListener('keydown', this.handleKeyDown);
    this.section.setAttribute('tabindex', '0');
    this.section.setAttribute('role', 'tablist');
  }

  /**
   * Initialize Intersection Observer for performance
   */
  initializeIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px 0px -20% 0px', // More strict - requires 80% of item to be visible
      threshold: [0.8, 1.0] // Trigger when 80% and 100% visible
    };

    this.intersectionObserver = new IntersectionObserver(this.handleIntersection, options);
    
    this.milestoneItems.forEach(item => {
      this.intersectionObserver.observe(item);
    });
  }

  /**
   * Set initial state of the component
   */
  setInitialState() {
    // Set first item as active
    this.setActiveItem(0);
    
    // Make first item visible
    if (this.milestoneItems[0]) {
      this.milestoneItems[0].classList.add('is-visible');
    }
  }

  /**
   * Handle year button clicks
   */
  handleYearClick(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const targetId = button.getAttribute('data-target');
    const targetElement = this.section.querySelector(targetId);
    
    if (!targetElement) {
      console.warn(`Target element not found: ${targetId}`);
      return;
    }

    const targetIndex = this.milestoneItems.findIndex(item => item.id === targetElement.id);
    
    if (targetIndex === -1) {
      console.warn(`Target index not found for element: ${targetElement.id}`);
      return;
    }

    // Set user scrolling flag to prevent scroll handler interference
    this.isUserScrolling = true;
    
    // Immediately set the clicked year as active
    this.setActiveItem(targetIndex);
    
    // Scroll to align the content perfectly with the year button
    this.scrollToItem(targetIndex, { alignWithButton: true });
  }

  /**
   * Handle keyboard navigation
   */
  handleKeyDown(event) {
    const { key, target } = event;
    
    // Handle arrow key navigation
    if (key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight') {
      event.preventDefault();
      
      const currentIndex = this.activeIndex;
      let newIndex = currentIndex;
      
      if (key === 'ArrowUp' || key === 'ArrowLeft') {
        newIndex = Math.max(0, currentIndex - 1);
      } else if (key === 'ArrowDown' || key === 'ArrowRight') {
        newIndex = Math.min(this.milestoneItems.length - 1, currentIndex + 1);
      }
      
      if (newIndex !== currentIndex) {
        // Set user scrolling flag to prevent scroll handler interference
        this.isUserScrolling = true;
        
        this.setActiveItem(newIndex);
        this.scrollToItem(newIndex, { smooth: true, focus: true, alignWithButton: true });
      }
    }
    
    // Handle Enter and Space on year buttons
    if ((key === 'Enter' || key === ' ') && target.hasAttribute('data-year-link')) {
      event.preventDefault();
      target.click();
    }
  }

  /**
   * Handle scroll events with performance optimization
   */
  handleScroll() {
    if (this.isScrolling || this.isUserScrolling) return;
    
    const currentTime = Date.now();
    
    // Add stability delay - don't change active state too quickly
    if (currentTime - this.lastScrollTime < 100) {
      return; // Skip if less than 100ms since last scroll event
    }
    
    this.lastScrollTime = currentTime;
    
    const viewportHeight = window.innerHeight;
    const sectionRect = this.section.getBoundingClientRect();
    
    // Check if section is in viewport
    if (sectionRect.bottom < 0 || sectionRect.top > viewportHeight) {
      return;
    }

    // Determine anchor point based on screen size
    const isMobile = window.innerWidth <= 1000;
    const anchorY = isMobile 
      ? viewportHeight / 2 
      : (this.yearsNav?.getBoundingClientRect().top || 120);

    // If section is near bottom, activate last item only if it's fully visible
    if (sectionRect.bottom <= viewportHeight - 50) {
      const lastItem = this.milestoneItems[this.milestoneItems.length - 1];
      const lastItemRect = lastItem.getBoundingClientRect();
      
      // Only activate last item if it's fully visible
      if (lastItemRect.top >= 0 && lastItemRect.bottom <= viewportHeight) {
        this.setActiveItem(this.milestoneItems.length - 1);
      }
      return;
    }

    // Find the most appropriate active item with much stricter requirements
    let bestIndex = this.activeIndex; // Default to current active - more conservative
    let bestScore = Infinity;

    this.milestoneItems.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      
      // Check if item is fully visible in viewport
      const isFullyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;
      
      // Check if item is at least 90% visible (even more strict)
      const itemHeight = rect.height;
      const visibleTop = Math.max(0, rect.top);
      const visibleBottom = Math.min(viewportHeight, rect.bottom);
      const visibleHeight = visibleBottom - visibleTop;
      const visibilityPercentage = visibleHeight / itemHeight;
      
      // Only consider items that are at least 90% visible AND fully visible
      if (visibilityPercentage < 0.9 || !isFullyVisible) {
        return; // Skip this item - too strict for fast scrolling
      }
      
      // Calculate distance from anchor point
      const itemCenter = rect.top + (rect.height / 2);
      const distance = Math.abs(itemCenter - anchorY);
      
      let score = distance;
      
      // Heavily penalize items that are above the anchor point (not yet reached)
      if (rect.top > anchorY + 30) { // Reduced from 50 to be more precise
        score += 2000; // Much higher penalty
      }
      
      // Prefer items that are perfectly positioned
      if (Math.abs(itemCenter - anchorY) < 50) { // Very close to anchor
        score -= 500; // Big bonus for perfect alignment
      }
      
      // Penalize items that are too far below the anchor
      if (rect.bottom < anchorY - 50) { // Reduced from 100
        score += 1000;
      }
      
      // Only update if this item has a significantly better score
      if (score < bestScore - 100 && visibilityPercentage >= 0.9 && isFullyVisible) {
        bestScore = score;
        bestIndex = index;
      }
    });

    // Only change active item if we found a significantly better replacement
    if (bestIndex !== this.activeIndex) {
      this.setActiveItem(bestIndex);
    }
  }

  /**
   * Handle intersection observer callbacks
   */
  handleIntersection(entries) {
    entries.forEach(entry => {
      // Only add is-visible class when the item is at least 80% visible
      if (entry.isIntersecting && entry.intersectionRatio >= 0.8) {
        entry.target.classList.add('is-visible');
      } else if (!entry.isIntersecting) {
        // Remove is-visible class when item is no longer intersecting
        entry.target.classList.remove('is-visible');
      }
    });
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    // Check if we need to switch between slider and scroll modes
    const wasSliderMode = this.isSliderMode;
    this.checkSliderMode();
    
    if (wasSliderMode !== this.isSliderMode) {
      // Mode changed, reinitialize
      this.destroy();
      this.init();
      return;
    }
    
    if (!this.isSliderMode) {
      // Recalculate active item on resize for scroll mode
      this.handleScroll();
    }
  }

  /**
   * Check if we should be in slider mode based on screen size
   */
  checkSliderMode() {
    this.isSliderMode = window.innerWidth <= 1000;
  }

  /**
   * Initialize slider mode
   */
  initializeSlider() {
    this.createSliderStructure();
    this.setupSliderEventListeners();
    this.updateSliderPosition();
  }

  /**
   * Create slider HTML structure
   */
  createSliderStructure() {
    const content = this.section.querySelector('[data-milestone-content]');
    if (!content) return;

    // Create slider container
    this.sliderContainer = document.createElement('div');
    this.sliderContainer.className = 'milestone__slider-container';
    this.sliderContainer.innerHTML = `
      <div class="milestone__slider-years" data-slider-years>
        ${this.yearsNav.innerHTML}
      </div>
      <div class="milestone__slider-wrapper" data-slider-wrapper>
        ${content.innerHTML}
      </div>
      <div class="milestone__slider-controls">
        <button class="milestone__slider-btn milestone__slider-prev" data-slider-prev aria-label="Previous milestone">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="milestone__slider-dots" data-slider-dots></div>
        <button class="milestone__slider-btn milestone__slider-next" data-slider-next aria-label="Next milestone">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;

    // Replace content with slider
    content.parentNode.replaceChild(this.sliderContainer, content);
    
    // Cache slider elements
    this.sliderWrapper = this.sliderContainer.querySelector('[data-slider-wrapper]');
    this.sliderPrevBtn = this.sliderContainer.querySelector('[data-slider-prev]');
    this.sliderNextBtn = this.sliderContainer.querySelector('[data-slider-next]');
    this.sliderYearsNav = this.sliderContainer.querySelector('[data-slider-years]');
    
    // Re-cache year buttons for slider mode
    this.sliderYearButtons = Array.from(this.sliderYearsNav.querySelectorAll('[data-year-link]'));
    
    // Create dots
    this.createSliderDots();
  }

  /**
   * Create slider dots
   */
  createSliderDots() {
    const dotsContainer = this.sliderContainer.querySelector('[data-slider-dots]');
    this.sliderDots = [];
    
    this.milestoneItems.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = 'milestone__slider-dot';
      dot.setAttribute('data-slider-dot', index);
      dot.setAttribute('aria-label', `Go to milestone ${index + 1}`);
      if (index === 0) dot.classList.add('is-active');
      
      this.sliderDots.push(dot);
      dotsContainer.appendChild(dot);
    });
  }

  /**
   * Setup slider event listeners
   */
  setupSliderEventListeners() {
    // Touch events for swipe
    this.sliderWrapper.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.sliderWrapper.addEventListener('touchmove', this.handleTouchMove, { passive: true });
    this.sliderWrapper.addEventListener('touchend', this.handleTouchEnd, { passive: true });

    // Button events
    this.sliderPrevBtn.addEventListener('click', this.handleSliderPrev);
    this.sliderNextBtn.addEventListener('click', this.handleSliderNext);

    // Dot events
    this.sliderDots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.handleDotClick(index));
    });

    // Year button events for slider mode
    if (this.sliderYearButtons) {
      this.sliderYearButtons.forEach((button, index) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.goToSlide(index);
        });
        button.addEventListener('keydown', this.handleKeyDown);
      });
    }

    // Keyboard navigation
    this.sliderContainer.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Handle touch start
   */
  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
  }

  /**
   * Handle touch move
   */
  handleTouchMove(event) {
    this.touchEndX = event.touches[0].clientX;
  }

  /**
   * Handle touch end
   */
  handleTouchEnd() {
    if (!this.touchStartX || !this.touchEndX) return;
    
    const diff = this.touchStartX - this.touchEndX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swipe left - next
        this.goToSlide(this.activeIndex + 1);
      } else {
        // Swipe right - previous
        this.goToSlide(this.activeIndex - 1);
      }
    }

    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  /**
   * Handle slider previous button
   */
  handleSliderPrev() {
    this.goToSlide(this.activeIndex - 1);
  }

  /**
   * Handle slider next button
   */
  handleSliderNext() {
    this.goToSlide(this.activeIndex + 1);
  }

  /**
   * Handle dot click
   */
  handleDotClick(index) {
    this.goToSlide(index);
  }

  /**
   * Go to specific slide
   */
  goToSlide(index) {
    if (this.sliderTransition) return;
    
    const maxIndex = this.milestoneItems.length - 1;
    const newIndex = Math.max(0, Math.min(index, maxIndex));
    
    if (newIndex === this.activeIndex) return;
    
    this.sliderTransition = true;
    this.setActiveItem(newIndex);
    this.updateSliderPosition();
    
    setTimeout(() => {
      this.sliderTransition = false;
    }, 300);
  }

  /**
   * Update slider position
   */
  updateSliderPosition() {
    if (!this.sliderWrapper) return;
    
    const translateX = -this.activeIndex * 100;
    this.sliderWrapper.style.transform = `translateX(${translateX}%)`;
    
    // Update dots
    this.sliderDots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.activeIndex);
    });
    
    // Update buttons
    this.sliderPrevBtn.disabled = this.activeIndex === 0;
    this.sliderNextBtn.disabled = this.activeIndex === this.milestoneItems.length - 1;
  }

  /**
   * Set the active item and update UI
   */
  setActiveItem(index) {
    if (index < 0 || index >= this.milestoneItems.length) return;
    
    this.activeIndex = index;
    
    if (this.isSliderMode) {
      // Update slider year buttons (if they exist)
      if (this.sliderYearButtons) {
        this.sliderYearButtons.forEach((button, buttonIndex) => {
          const isActive = buttonIndex === index;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', isActive.toString());
          button.setAttribute('tabindex', isActive ? '0' : '-1');
        });
      }
      
      // Update slider dots
      this.sliderDots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
      });
      
      // Update milestone items in slider
      this.milestoneItems.forEach((item, itemIndex) => {
        item.classList.toggle('is-active-content', itemIndex === index);
      });
    } else {
      // Update year buttons (desktop mode)
      this.yearButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive.toString());
        button.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      
      // Update milestone items
      this.milestoneItems.forEach((item, itemIndex) => {
        item.classList.toggle('is-active-content', itemIndex === index);
      });
    }
  }

  /**
   * Scroll to a specific milestone item
   */
  scrollToItem(index, options = {}) {
    if (index < 0 || index >= this.milestoneItems.length) return;
    
    const targetItem = this.milestoneItems[index];
    const { smooth = true, focus = false, alignWithButton = false } = options;
    
    this.isScrolling = true;
    
    // Calculate scroll position
    const isMobile = window.innerWidth <= 1000;
    let offsetY;
    
    if (alignWithButton && this.yearsNav) {
      // Perfect alignment: position content so it aligns with the year button
      const yearsNavRect = this.yearsNav.getBoundingClientRect();
      const targetYearButton = this.yearButtons[index];
      
      if (targetYearButton) {
        const buttonRect = targetYearButton.getBoundingClientRect();
        // Calculate the vertical center of the year button
        const buttonCenterY = buttonRect.top + (buttonRect.height / 2);
        
        // Position the content so its top aligns with the button center
        offsetY = buttonCenterY - 20; // Small offset for better visual alignment
      } else {
        offsetY = yearsNavRect.top - 10;
      }
    } else {
      // Standard positioning
      if (isMobile) {
        offsetY = 100;
      } else {
        const anchorY = this.yearsNav?.getBoundingClientRect().top || 120;
        offsetY = anchorY - 10;
      }
    }
    
    const targetRect = targetItem.getBoundingClientRect();
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
    const targetScrollY = currentScrollY + targetRect.top - offsetY;
    
    // Smooth scroll to target
    window.scrollTo({
      top: Math.max(0, targetScrollY),
      behavior: smooth ? 'smooth' : 'auto'
    });
    
    // Focus the target item if requested
    if (focus) {
      targetItem.focus();
    }
    
    // Reset scrolling flags after animation with additional delay for user actions
    setTimeout(() => {
      this.isScrolling = false;
    }, 1000);
    
    // Reset user scrolling flag with additional delay to prevent interference
    setTimeout(() => {
      this.isUserScrolling = false;
    }, 1500);
  }

  /**
   * Utility: Throttle function for performance
   */
  throttle(func, delay) {
    let timeoutId;
    let lastExecTime = 0;
    
    return function (...args) {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }

  /**
   * Utility: Debounce function for performance
   */
  debounce(func, delay) {
    let timeoutId;
    
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }

  /**
   * Cleanup method to remove event listeners and observers
   */
  destroy() {
    if (!this.isInitialized) return;
    
    if (this.isSliderMode) {
      // Remove slider event listeners
      if (this.sliderWrapper) {
        this.sliderWrapper.removeEventListener('touchstart', this.handleTouchStart);
        this.sliderWrapper.removeEventListener('touchmove', this.handleTouchMove);
        this.sliderWrapper.removeEventListener('touchend', this.handleTouchEnd);
      }
      
      if (this.sliderPrevBtn) {
        this.sliderPrevBtn.removeEventListener('click', this.handleSliderPrev);
      }
      
      if (this.sliderNextBtn) {
        this.sliderNextBtn.removeEventListener('click', this.handleSliderNext);
      }
      
      this.sliderDots.forEach(dot => {
        dot.removeEventListener('click', this.handleDotClick);
      });
      
      // Remove slider year button event listeners
      if (this.sliderYearButtons) {
        this.sliderYearButtons.forEach(button => {
          button.removeEventListener('click', this.handleYearClick);
          button.removeEventListener('keydown', this.handleKeyDown);
        });
      }
      
      if (this.sliderContainer) {
        this.sliderContainer.removeEventListener('keydown', this.handleKeyDown);
      }
    } else {
      // Remove scroll mode event listeners
      this.yearButtons.forEach(button => {
        button.removeEventListener('click', this.handleYearClick);
        button.removeEventListener('keydown', this.handleKeyDown);
      });
      
      this.section.removeEventListener('keydown', this.handleKeyDown);
      
      // Disconnect intersection observer
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    }
    
    // Remove common event listeners
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    
    this.isInitialized = false;
    console.log(`Milestone section ${this.sectionId} destroyed`);
  }
}

/**
 * Initialize milestone sections
 */
function initMilestoneSections() {
  const sections = document.querySelectorAll('[id^="Milestone-"]');
  
  sections.forEach(section => {
    try {
      new MilestoneSection(section);
    } catch (error) {
      console.error(`Failed to initialize milestone section ${section.id}:`, error);
    }
  });
}

/**
 * Initialize on DOM ready
 */
document.addEventListener('DOMContentLoaded', initMilestoneSections);

/**
 * Handle Shopify theme editor
 */
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (section && section.id && section.id.startsWith('Milestone-')) {
      try {
        new MilestoneSection(section);
      } catch (error) {
        console.error(`Failed to initialize milestone section ${section.id}:`, error);
      }
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    if (section && section.id && section.id.startsWith('Milestone-')) {
      // Find and destroy the instance if it exists
      const instances = section._milestoneInstance;
      if (instances && instances.destroy) {
        instances.destroy();
      }
    }
  });
}

/**
 * Handle page visibility changes for performance
 */
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    // Pause any ongoing animations or timers when page is hidden
    console.log('Page hidden - milestone sections paused');
  } else {
    // Resume functionality when page becomes visible
    console.log('Page visible - milestone sections resumed');
  }
});